/**
 * The screen's half of the OPT-OUT MASTER WRITE (William, 22 Oct 2026, BN1 a; api/src/lib/optout-everywhere.ts), behind
 * MEMBERCARE_OPTOUT_MASTER_WRITE only (features.optOutMasterWrite). At approval Care also sends the member's opt-out
 * decision to the master database. When that send failed, the case says so in a note (data.kind optout-everywhere, or
 * optout-everywhere-master on a retry) whose data.master names the outcome and the batch. The drawer:
 *   1. a RETRYABLE failure (no answer, or never sent because Care's door settings were missing): says plainly that the
 *      master did not receive the decision and offers Approve (retry); the server resends the same batch (event-review.ts
 *      retryLateStop, masterRetriesOf) and approves nothing new;
 *   2. a REFUSED batch (final; QA-1705 A3): the server's sentence, and NO retry button (Approve again cannot clear it).
 * Switch off: nothing here is shown. The server decides; this file only reads what it wrote. NO em dashes.
 */
import type { EventAction } from '@lib/care-model';

/** The case notes' data.kind values that carry a master outcome (api/src/lib/optout-everywhere.ts). */
export const MASTER_NOTE_KINDS = ['optout-everywhere', 'optout-everywhere-master'] as const;

export const MASTER_RETRY_WORDING = {
  head: 'Opt-out not recorded in the master database',
  failed: 'The master database did not receive this decision. Press Approve (retry) to send it again.',
  /** QA-1705 A3, the server's own sentence (api MASTER_REFUSED_FINAL_WORDS). */
  refused: 'The master refused this decision; pressing Approve again will not change it. Tell lane 05.',
  sent: 'The decision was sent to the master database.',
  retry: 'Approve (retry)',
  retrying: 'Sending...',
} as const;

interface MasterOutcome { status?: unknown; code?: unknown; batch?: unknown }

/** A failed master send Approve again can still fix (mirrors api masterRetryable). */
function retryable(m: MasterOutcome): boolean {
  return m.status === 'unknown' || (m.status === 'refused' && m.code === 'master_door_not_configured');
}

export type MasterSendState = 'retry' | 'refused' | null;

/**
 * The case's master state, read from its notes: the LATEST outcome of each decision written under the master write
 * (master.batch present). 'retry' when any is retryable, else 'refused' when any is refused, else null. Only for an
 * approved case (cleared, or closed with no action).
 */
export function masterSendState(ev: { status?: string; actions?: ReadonlyArray<Pick<EventAction, 'type' | 'data'>> | null } | null | undefined): MasterSendState {
  if (!ev || (ev.status !== 'cleared' && ev.status !== 'closed-no-action')) return null;
  const latest = new Map<string, MasterOutcome>();
  for (const a of ev.actions ?? []) {
    const d = (a.data ?? {}) as Record<string, unknown>;
    if ((a.type as string) !== 'note' || !(MASTER_NOTE_KINDS as readonly unknown[]).includes(d['kind'])) continue;
    const m = d['master'] as MasterOutcome | undefined;
    const key = String(d['sourceTs'] ?? '');
    if (!m || !key) continue;
    if (d['kind'] === 'optout-everywhere' && (typeof m.batch !== 'string' || !m.batch)) continue;
    if (d['kind'] === 'optout-everywhere-master' && !latest.has(key)) continue;
    latest.set(key, m);
  }
  const all = [...latest.values()];
  if (all.some(retryable)) return 'retry';
  if (all.some((m) => m.status === 'refused')) return 'refused';
  return null;
}

/** The same reading on an Approve answer (answer.optOutEverywhere.decisions[].master). */
export function masterAnswerState(answer: unknown): MasterSendState {
  const ds = (answer as { optOutEverywhere?: { decisions?: Array<{ master?: MasterOutcome }> } } | null | undefined)?.optOutEverywhere?.decisions;
  const ms = (Array.isArray(ds) ? ds : []).map((d) => d.master ?? {}).filter((m) => typeof m.batch === 'string' && m.batch);
  if (ms.some(retryable)) return 'retry';
  if (ms.some((m) => m.status === 'refused')) return 'refused';
  return null;
}

/** The sentence an Approve answer adds about the master (switch on only), '' when nothing failed. */
export function masterAnswerWords(answer: unknown): string {
  const s = masterAnswerState(answer);
  return s === 'retry' ? MASTER_RETRY_WORDING.failed : s === 'refused' ? MASTER_RETRY_WORDING.refused : '';
}
